import React, { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Send, X, ArrowLeft } from 'lucide-react';
import MessageTemplates from './MessageTemplates';
import { toast } from 'sonner';

interface MessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipient: {
    name: string;
    avatar: string;
    verified?: boolean;
  };
  subject: {
    title: string;
    type: 'service' | 'product';
    price?: number;
    image?: string;
  };
  onSendMessage: (message: string) => void;
}

const MessageModal = ({ isOpen, onClose, recipient, subject, onSendMessage }: MessageModalProps) => {
  const [step, setStep] = useState<'templates' | 'compose'>('templates');
  const [message, setMessage] = useState('');

  const handleSelectTemplate = (template: string) => {
    setMessage(template);
    setStep('compose');
  };

  const handleSendMessage = () => {
    if (!message.trim()) {
      toast.error('Schrijf eerst een bericht');
      return;
    }

    console.log('Sending message:', { recipient: recipient.name, message, subject: subject.title });
    onSendMessage(message);
    
    // Reset and close
    setMessage('');
    setStep('templates');
    onClose();
    
    toast.success(`Bericht verstuurd naar ${recipient.name}!`);
  };

  const handleClose = () => {
    setMessage('');
    setStep('templates');
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="text-lg font-semibold text-gray-900">
              {step === 'templates' ? 'Nieuw Bericht' : 'Bericht Versturen'}
            </DialogTitle>
            {step === 'compose' && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setStep('templates')}
                className="mr-8"
              >
                <ArrowLeft className="h-4 w-4" />
              </Button>
            )}
          </div>
        </DialogHeader>

        {/* Recipient Info */}
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="h-10 w-10">
              <AvatarImage src={recipient.avatar} />
              <AvatarFallback className="bg-rose-100 text-rose-700">
                {recipient.name.split(' ').map(n => n[0]).join('')}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{recipient.name}</h3>
                {recipient.verified && (
                  <Badge variant="secondary" className="bg-rose-100 text-rose-700 text-xs">
                    Verified
                  </Badge>
                )}
              </div>
              <p className="text-sm text-gray-600">Professional Woman</p>
            </div>
          </div>

          {/* Subject Context */}
          <div className="flex items-center gap-3 p-3 bg-white rounded-lg border">
            {subject.image && (
              <img 
                src={subject.image} 
                alt={subject.title}
                className="w-12 h-12 object-cover rounded-lg"
              />
            )}
            <div className="flex-1 min-w-0">
              <p className="font-medium text-gray-900 text-sm truncate">{subject.title}</p>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant="outline" className="text-xs">
                  {subject.type === 'service' ? 'Dienst' : 'Product'}
                </Badge>
                {subject.price && (
                  <span className="text-sm font-medium text-rose-600">€{subject.price}</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {step === 'templates' ? (
          <MessageTemplates
            onSelectTemplate={handleSelectTemplate}
            serviceTitle={subject.type === 'service' ? subject.title : undefined}
            productTitle={subject.type === 'product' ? subject.title : undefined}
            type={subject.type}
          />
        ) : (
          <div className="space-y-4">
            <div>
              <label className="text-sm font-medium text-gray-700 mb-2 block">
                Je bericht:
              </label>
              <Textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Schrijf je bericht hier..."
                className="min-h-[120px] resize-none"
                autoFocus
              />
            </div>

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={() => setStep('templates')}
                className="flex-1"
              >
                Template Kiezen
              </Button>
              <Button
                onClick={handleSendMessage}
                disabled={!message.trim()}
                className="flex-1 bg-rose-600 hover:bg-rose-700"
              >
                <Send className="h-4 w-4 mr-2" />
                Versturen
              </Button>
            </div>
          </div>
        )}

        {step === 'templates' && (
          <div className="mt-4 p-3 bg-rose-50 rounded-lg">
            <p className="text-sm text-rose-800">
              <strong>Tip:</strong> Professionele berichten krijgen sneller antwoord. 
              Gebruik onze templates voor de beste resultaten!
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default MessageModal;